import { Component, Inject, OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

export interface MoveBittingsDialogData {
  sourceBookId: number
  selectedBittings: any[]
  keySystemId: number
  masterKeyId: number
  masterKeys: any[]
}

export interface MoveBittingsDialogResult {
  destinationType: 'new' | 'existing'
  destinationBookId?: number
  destinationKeyId?: number
  selectedBittings: any[]
}

@Component({
  selector: 'app-move-bittings-dialog',
  templateUrl: './move-bittings-dialog.component.html',
  styleUrls: ['./move-bittings-dialog.component.scss'],
  standalone: false,
})
export class MoveBittingsDialogComponent implements OnInit {
  moveForm: FormGroup
  sourceBookId: number
  selectedBittings: any[]
  masterKeys: any[] = []
  filteredKeys: any[] = []
  destinationType: 'new' | 'existing' = 'new'

  constructor(
    private form: FormBuilder,
    private dialogRef: MatDialogRef<MoveBittingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MoveBittingsDialogData
  ) {
    this.moveForm = this.form.group({
      destinationType: new FormControl('new'),
      destinationBookId: new FormControl('', [Validators.pattern(/^\d+$/)]),
      destinationKeyId: new FormControl(''),
    })
  }

  ngOnInit() {
    this.sourceBookId = this.data.sourceBookId
    this.selectedBittings = this.data.selectedBittings
    this.masterKeys = this.data.masterKeys || []
    this.filteredKeys = [...this.masterKeys]

    // Filter keys based on input
    this.moveForm.get('destinationKeyId')?.valueChanges.subscribe((value) => {
      this.filterKeys(value)
    })

    // Custom validator to check if destination is same as source
    const sameAsSourceValidator = (control: AbstractControl): ValidationErrors | null => {
      if (control.value && Number(control.value) === this.sourceBookId) {
        return { sameAsSource: true }
      }
      return null
    }

    // Set initial validator for destinationKeyId (required when 'new' is selected)
    this.moveForm.get('destinationKeyId')?.setValidators([Validators.required])

    // Update validators based on destination type
    this.moveForm.get('destinationType')?.valueChanges.subscribe((type) => {
      this.destinationType = type
      const bookIdControl = this.moveForm.get('destinationBookId')
      const keyIdControl = this.moveForm.get('destinationKeyId')

      if (type === 'existing') {
        bookIdControl?.setValidators([Validators.required, Validators.pattern(/^\d+$/), sameAsSourceValidator])
        keyIdControl?.clearValidators()
      } else {
        bookIdControl?.clearValidators()
        keyIdControl?.setValidators([Validators.required])
      }
      bookIdControl?.updateValueAndValidity()
      keyIdControl?.updateValueAndValidity()
    })
  }

  get selectedKeyName(): string {
    const value = this.moveForm.get('destinationKeyId')?.value
    if (!value) return ''
    // If value is an object (selected from autocomplete), get keySetSymbol directly
    if (typeof value === 'object' && value?.keySetSymbol) {
      return value.keySetSymbol
    }
    // If value is an id, find the key
    const key = this.masterKeys.find((k) => k.id === value)
    return key?.keySetSymbol || ''
  }

  filterKeys(value: any): void {
    if (!value || typeof value === 'object') {
      this.filteredKeys = [...this.masterKeys]
      return
    }
    const filterValue = value.toLowerCase()
    this.filteredKeys = this.masterKeys.filter((key) =>
      key.keySetSymbol?.toLowerCase().startsWith(filterValue)
    )
  }

  displayKeyName(key: any): string {
    return key?.keySetSymbol || ''
  }

  isFormValid(): boolean {
    if (this.destinationType === 'new') {
      const keyValue = this.moveForm.get('destinationKeyId')?.value
      // Valid if it's an object with id (selected from autocomplete)
      return keyValue && typeof keyValue === 'object' && keyValue.id
    }
    return this.moveForm.get('destinationBookId')?.valid ?? false
  }

  move() {
    if (this.isFormValid()) {
      const result: MoveBittingsDialogResult = {
        destinationType: this.destinationType,
        selectedBittings: this.selectedBittings,
      }

      if (this.destinationType === 'new') {
        const keyValue = this.moveForm.get('destinationKeyId')?.value
        result.destinationKeyId = typeof keyValue === 'object' ? keyValue.id : Number(keyValue)
      } else {
        result.destinationBookId = Number(this.moveForm.get('destinationBookId')?.value)
      }

      this.dialogRef.close(result)
    }
  }

  onBookIdKeyUp(): void {
    const control = this.moveForm.get('destinationBookId')
    if (control && Number(control.value) === this.sourceBookId) {
      control.markAsTouched()
    }
  }

  close() {
    this.dialogRef.close()
  }
}
